import React, { useCallback, useMemo } from 'react';
import querystring from 'querystring';
import { ProviderProfile } from '@kyruus/provider-profile';
import { injectIntl } from '@kyruus/intl';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWaitTimeByLocationID } from '../behaviors/search/actions';
import { ProfileWrapper, ProfileContainer } from './styles';
import SearchLinks from './search-links';
import LoadingOverlay from '../shared/loading';
import PoweredByLogo from '../shared/powered-by-logo';
import Error from '../error';
import ProfileMetas from './profile-metas';
import { useAppDrawer, APP_DRAWER_IDS } from 'Src/app-drawer';
import { CtaDrawer } from 'Src/cta-drawer';

import { messages } from './lib/messages';
import { SLOTS_LOADING_STATUS } from '../utils/constants';
import { buildLocationURL, locationsModuleEnabled } from '../utils/location';
import { isCCF } from '../utils/cerner';
import {
  canSkipModal,
  shouldRenderBookingBtnAsLink,
  isApptInfoValid
} from './lib/utils';
import { getRelativeParameterizedBookingUrl } from '../utils/getRelativeParameterizedBookingUrl';
import {
  useTracking,
  useProfileCtaModal,
  useProfileAvailability
} from './lib/hooks';
import { getProviderSummaryUrl } from '../provider/utils';
import {
  MODULES,
  isModuleEnabled,
  shouldRenderDirectBookInDrawer,
  getConfigProperty
} from 'Common/config';
import { BOOK_TRACKING_VALUE_PROFILE } from 'Src/tracking/constants';
import { splitObjectKeys } from '../search-v9/utils';
import { V9_PATIENT_REL_FILTER_KEY } from '../utils/constants';
import { getCtaConfig, getRenderableCtas } from '@kyruus/provider-components';

const ProfileV9 = ({
  providerId,
  profile,
  currentQuery,
  customerCode,
  config,
  loading,
  error,
  log,
  location,
  fetchPurposesByProviderId,
  fetchSlots,
  slotsByProviderId,
  slotsStatus: slotsStatusByProviderId,
  availabilityError,
  availabilityLoading,
  purposeOptionsByProviderId,
  purposesLoading,
  history,
  device,
  intl,
  match,
  appSettings,
  fetchProviderAvailability
}) => {
  const dispatch = useDispatch();
  const waitTimeMap = useSelector((state) => state.search?.waitTimeMap);
  const isCtaDrawerEnabled = getConfigProperty(
    config,
    'darkship_feature_flags.use_cta_drawer',
    false
  );
  const { closeAppDrawer, openAppDrawer } = useAppDrawer();
  const {
    provider,
    locations,
    availability,
    altProviders,
    careTeamProviders,
    mlocationsById
  } = profile || {};

  const trackProviderProfileEvent = useCallback(
    (eventName, eventData) => {
      const additionalEventData = {
        ...eventData
      };

      if (provider?.specialties) {
        additionalEventData.provider_specialties = provider?.specialties.map(
          (s) => s.name
        );
      }

      return log(eventName, additionalEventData);
    },
    [log, provider?.specialties]
  );

  useTracking({
    log: trackProviderProfileEvent,
    config,
    providerId,
    profile,
    error,
    loading
  });

  const urlQuerystring = location.search;
  const queryParams = querystring.parse(
    urlQuerystring.substring(1, urlQuerystring.length)
  );

  // Handle both single filter (string) and multiple filters (array)
  let searchFilters = [];
  if (queryParams.filter) {
    if (Array.isArray(queryParams.filter) && queryParams.filter.length) {
      // Multiple filters case
      searchFilters = queryParams.filter;
    } else if (typeof queryParams.filter === 'string') {
      // Single filter case
      searchFilters = [queryParams.filter];
    }
  }

  // Convert to object format for easier access
  const querystringFilters =
    searchFilters.length > 0
      ? Object.assign(...splitObjectKeys(searchFilters))
      : null;

  const shouldOpenDirectBookSameWindow = isModuleEnabled(
    config,
    MODULES.DIRECT_BOOK
  );

  const bookingLinkTarget = shouldOpenDirectBookSameWindow ? '_self' : null;

  const { modal_display } = config;
  const isVirtualCare = modal_display?.virtual_care;

  const slots =
    slotsByProviderId && slotsByProviderId[providerId]
      ? slotsByProviderId[providerId]?.slots
      : null;

  const initApptInfo = (() => {
    if (slotsByProviderId[providerId]?.apptInfo) {
      return slotsByProviderId[providerId]?.apptInfo;
    }

    // Check direct URL parameters (existing logic)
    if (queryParams.relationship && queryParams.purpose) {
      return {
        relationship: queryParams.relationship,
        purpose: queryParams.purpose
      };
    }

    // Check filter-based parameters (like search filters)
    if (querystringFilters) {
      const filterPatientRel = querystringFilters[V9_PATIENT_REL_FILTER_KEY];

      if (
        filterPatientRel &&
        (filterPatientRel === 'new' || filterPatientRel === 'established')
      ) {
        return {
          relationship: filterPatientRel,
          purpose: queryParams.purpose || undefined // Use purpose if available from URL params
        };
      }
    }

    return undefined;
  })();

  const slotsStatus =
    slotsStatusByProviderId && slotsStatusByProviderId[providerId]
      ? slotsStatusByProviderId[providerId]
      : SLOTS_LOADING_STATUS;

  /**
   * Creates an availability URL for the given alt provider or care team provider.
   * @param {Object} provider - The provider object.
   * @returns {string|null} The availability URL or null if provider is not provided.
   */
  const createAvailabilityUrl = (provider) => {
    if (!provider) {
      return null;
    }

    return (
      getRelativeParameterizedBookingUrl({
        provider,
        config,
        location,
        apptOptions: {},
        ignoreApptOptions: true
      }) || provider.virtual_care_url
    );
  };

  /*
   * Returns the URL that the user will be directed to when booking.
   * Used as the `href` for buttons that will be handled by the callback,
   * or the anchor tag when customer config has button be a direct link.
   */
  const getDirectBookHref = (provider) => {
    if (!provider) {
      return null;
    }

    const relativeParameterizedBookingUrl = getRelativeParameterizedBookingUrl({
      provider,
      config,
      location,
      apptOptions: initApptInfo
    });

    if (relativeParameterizedBookingUrl) {
      return relativeParameterizedBookingUrl;
    }

    return provider.virtual_care_url;
  };

  const directBookHref = getDirectBookHref(provider);

  const { profileCtaModal, setProfileCtaModalIsOpen } = useProfileCtaModal({
    profile,
    bookingLinkTarget,
    directBookHref,
    isVirtualCare,
    config,
    log: trackProviderProfileEvent
  });

  const {
    apptInfo,
    availabilityByLocationId,
    handleTimeSlotsClick,
    handleViewMoreClick,
    editApptInfoCallback,
    patientRelOptions,
    bookingInfoLoading
  } = useProfileAvailability({
    availabilityError,
    availabilityLoading,
    config,
    directBookHref,
    fetchPurposesByProviderId,
    fetchSlots,
    history,
    intl,
    location,
    log: trackProviderProfileEvent,
    profile,
    providerId,
    purpose: initApptInfo?.purpose,
    purposeOptionsByProviderId,
    relationship: initApptInfo?.relationship,
    slots,
    slotsStatus
  });

  const bookingCallback = useCallback(() => {
    if (!canSkipModal(provider, config)) {
      setProfileCtaModalIsOpen(true);
      openAppDrawer(APP_DRAWER_IDS.CTA_DRAWER);
      return;
    }
    if (shouldRenderDirectBookInDrawer(config)) {
      history.push(directBookHref, { provider: { location, provider } });
      return;
    }
  }, [
    config,
    provider,
    history,
    directBookHref,
    location,
    setProfileCtaModalIsOpen,
    openAppDrawer
  ]);

  const getWaitTimeCallbackFunction = async (
    locationEhrId,
    sourceSystem,
    bookAsTypeIds
  ) => {
    return dispatch(
      fetchWaitTimeByLocationID(locationEhrId, sourceSystem, bookAsTypeIds)
    );
  };

  const locationCallback = useCallback(
    (locationUrl, isExternalUrl = false) => {
      trackProviderProfileEvent('user_action.display_location_profile_page');

      if (isExternalUrl && typeof window !== 'undefined') {
        window.open(locationUrl, '_blank', 'noopener');
      }
      if (!isExternalUrl) {
        history.push(locationUrl);
      }
    },
    [history, trackProviderProfileEvent]
  );

  const ctaDrawerItems = useMemo(() => {
    return isCtaDrawerEnabled && provider
      ? getRenderableCtas({
          ctaConfig: getCtaConfig(config, apptInfo?.relationship),
          provider,
          patientRelationship: apptInfo?.relationship
        })
      : [];
  }, [config, isCtaDrawerEnabled, provider, apptInfo?.relationship]);

  if (!loading && (error || !profile)) {
    return (
      <Error
        config={config}
        customerCode={customerCode}
        history={history}
        log={trackProviderProfileEvent}
      />
    );
  }

  return (
    <ProfileWrapper>
      <ProfileContainer>
        <LoadingOverlay loading={loading} />
        {profile && (
          <ProfileMetas profile={profile} config={config} messages={messages} />
        )}
        <SearchLinks
          currentQuery={currentQuery}
          log={trackProviderProfileEvent}
          profile={profile}
          config={config}
          device={device}
        />
        {!loading && profile && (
          <ProviderProfile
            handleTimeSlotsClick={handleTimeSlotsClick}
            handleViewMoreClick={handleViewMoreClick}
            /*
             * The patient relationship and appointment purpose for the provider's availability tiles.
             * This data indicates the user has searched by appointment, and will be used in displaying
             * the availability view
             */
            apptInfo={isApptInfoValid(apptInfo) ? apptInfo : undefined}
            /**
             * A map of location id's to their availability. Used to determine which locations to render availability for.
             * If undefined, assume we are in a loading or error state and display all locations.
             */
            availabilityByLocationId={availabilityByLocationId}
            /** Callback provided to the profile to with the new appointment info */
            editApptInfoCallback={editApptInfoCallback}
            /** Callback provided to the profile to handle users clicking the booking button */
            bookingCallback={bookingCallback}
            /**
             * The href for the booking CTA when it's rendered as an anchor tag
             * If this prop is undefined, the booking CTA will be rendered as a button
             */
            bookOnlineUrl={
              shouldRenderBookingBtnAsLink(provider, config) && directBookHref
                ? directBookHref
                : undefined
            }
            /** This url is also used to generate urls for availability-tiles */
            availTilesBookOnlineUrl={directBookHref || ''}
            /** The PMC config object, used for controlling certain behaviors of the profile */
            config={config}
            /** The locations array for the provider */
            locations={locations}
            /** The provider to display the profile of. */
            provider={provider}
            /** The availability_density_best value for this provider (https://kyruus.jira.com/wiki/spaces/AHC/pages/940441689/Feature+Availability+Density) */
            availabilityDensityBest={
              availability.meta.availability_density_best
            }
            /** The UTC date of the provider's soonest appointment */
            soonestAppointmentDate={availability.meta.min_date_available}
            /** Track function for analytics */
            track={trackProviderProfileEvent}
            /** Search filters the user has applied (passed from PMC) */
            filters={searchFilters}
            /** Function to build a provider's location's url */
            getLocationUrl={(l) =>
              buildLocationURL(
                locationsModuleEnabled(config),
                l,
                urlQuerystring
              )
            }
            /** Callback for when a provider's location anchor is clicked */
            locationCallback={locationCallback}
            getWaitTimeCallbackFunction={getWaitTimeCallbackFunction}
            waitTimeMap={waitTimeMap}
            /** Marketable locations for this provider */
            mlocations={mlocationsById}
            /** alternate providers to render (similar providers) */
            altProviders={altProviders}
            /** a provider's care team to render if enabled */
            careTeamProviders={careTeamProviders}
            /** function to create availability urls for each alt provider */
            createAvailabilityUrl={createAvailabilityUrl}
            /** function to create provider profile page urls for each alt provider */
            createProviderUrl={(provider) =>
              getProviderSummaryUrl(
                provider,
                '',
                slotsByProviderId,
                {}, // This should be empty for profile as it is unknown
                location,
                match
              )
            }
            shouldHidePageAnchors={isCCF()}
            /** whether or not to hide page anchor links */
            awsMapConfig={{
              identityPoolId:
                appSettings.AWS_MAPS_CREDENTIALS.mapIdentityPoolId,
              mapName: appSettings.AWS_MAPS_CREDENTIALS.mapName,
              region: appSettings.AWS_MAPS_CREDENTIALS.mapRegion
            }}
            /** provider purposes by relationship */
            purposeOptionsByRelationship={patientRelOptions}
            loading={{
              bookingInfo: bookingInfoLoading || purposesLoading
            }}
            /** function to grab provider slots for availability-tiles */
            fetchProviderAvailability={fetchProviderAvailability}
            /** necessary for tracking */
            bookSrc={BOOK_TRACKING_VALUE_PROFILE}
            /** only applies when darkship_feature_flags.use_cta_drawer is enabled */
            showBookingCtaButton={Boolean(ctaDrawerItems.length)}
          />
        )}
        <PoweredByLogo config={config} />
      </ProfileContainer>
      {!isCtaDrawerEnabled && profileCtaModal}
      {isCtaDrawerEnabled && provider && (
        <CtaDrawer
          onClose={closeAppDrawer}
          provider={provider}
          log={log}
          config={config}
          bookOnlineUrl={directBookHref}
          patientRelationship={apptInfo?.relationship}
        />
      )}
    </ProfileWrapper>
  );
};

export default injectIntl(ProfileV9);
